import { useRef, useState, type FormEvent } from "react";
import { Money } from "@shop-ledger/shared-types";
import { DataTable, usePagination, type DataTableColumn } from "@shop-ledger/ui-components";
import { useAuth } from "../../auth/AuthContext";
import { importBankStatement, listUnmatchedBankLines, matchBankLine, unmatchBankLine } from "../../api/bankReconciliationApi";
import type { CandidateLegOut, UnmatchedLineWithCandidatesOut } from "../../api/types";
import { AccountSelect } from "../../components/AccountSelect";
import { BranchSelect } from "../../components/BranchSelect";
import { StatusBanner } from "../../components/StatusBanner";
import { ApiError } from "../../lib/apiClient";
import { useAsyncData } from "../../lib/useAsyncData";
import { ROLES_WITH_BANK_RECON_ACCESS } from "../../roles/permissions";

const TENANT_CURRENCY_FALLBACK = "UGX"; // Tenant.currency isn't returned by GET /auth/me
// (UserOut, `@shop-ledger/shared-types`'s api/auth.ts) — same documented stand-in every
// other money-displaying page in this app uses (see e.g. DashboardPage.tsx).

/**
 * Bank reconciliation — CSV statement upload, unmatched-lines view with candidate matches,
 * match/unmatch actions (§13.3). OWNER/ACCOUNTANT-only on every route.
 */
export function BankReconciliationPage() {
  const { user, ensureFreshToken } = useAuth();
  const hasBankReconAccess = !!user?.role && (ROLES_WITH_BANK_RECON_ACCESS as readonly string[]).includes(user.role);

  const [accountId, setAccountId] = useState("");
  const [importBranchId, setImportBranchId] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<string | null>(null);

  const unmatchedState = useAsyncData(
    () => (accountId ? ensureFreshToken().then((token) => listUnmatchedBankLines(token, accountId)) : Promise.resolve([])),
    [accountId],
  );
  const { pageData: unmatchedPageData, pagination: unmatchedPagination, setPage: setUnmatchedPage } = usePagination(
    unmatchedState.data ?? [],
    25,
  );

  const [actionError, setActionError] = useState<string | null>(null);
  const [actingOn, setActingOn] = useState<string | null>(null);

  async function handleImport(event: FormEvent) {
    event.preventDefault();
    setImportError(null);
    setImportResult(null);
    if (!accountId) {
      setImportError("Select an account first.");
      return;
    }
    if (!file) {
      setImportError("Choose a CSV file.");
      return;
    }
    setImporting(true);
    try {
      const token = await ensureFreshToken();
      const result = await importBankStatement(token, { accountId, branchId: importBranchId || undefined, file });
      setImportResult(`Imported ${result.imported_count} line(s)${result.errors.length > 0 ? `, ${result.errors.length} row error(s)` : ""}.`);
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      unmatchedState.reload();
    } catch (error) {
      setImportError(error instanceof ApiError ? error.message : "Import failed.");
    } finally {
      setImporting(false);
    }
  }

  async function handleMatch(line: UnmatchedLineWithCandidatesOut, candidate: CandidateLegOut) {
    setActionError(null);
    setActingOn(line.line.id);
    try {
      const token = await ensureFreshToken();
      await matchBankLine(token, line.line.id, candidate.id);
      unmatchedState.reload();
    } catch (error) {
      setActionError(error instanceof ApiError ? error.message : "Could not record match.");
    } finally {
      setActingOn(null);
    }
  }

  const columns: DataTableColumn<UnmatchedLineWithCandidatesOut>[] = [
    { key: "date", header: "Date", render: (r) => r.line.transaction_date },
    { key: "description", header: "Description", render: (r) => r.line.description ?? "—" },
    { key: "amount", header: "Amount", align: "right", render: (r) => Money.format(r.line.amount, TENANT_CURRENCY_FALLBACK) },
    { key: "reference", header: "Reference", render: (r) => r.line.external_reference ?? "—" },
    {
      key: "candidates",
      header: "Candidate matches",
      render: (r) =>
        r.candidates.length === 0 ? (
          <span className="muted">No candidates in range</span>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {r.candidates.map((c) => (
              <button
                key={c.id}
                type="button"
                className="sl-button sl-button--secondary sl-button--md"
                disabled={actingOn === r.line.id}
                onClick={() => void handleMatch(r, c)}
              >
                Match leg {c.id.slice(0, 8)} (debit {Money.format(c.debit, TENANT_CURRENCY_FALLBACK)} / credit{" "}
                {Money.format(c.credit, TENANT_CURRENCY_FALLBACK)})
              </button>
            ))}
          </div>
        ),
    },
  ];

  if (!hasBankReconAccess) {
    return (
      <div className="page">
        <div className="page__header">
          <div>
            <h1 className="page__title">Bank Reconciliation</h1>
          </div>
        </div>
        <StatusBanner kind="info">Bank reconciliation is OWNER/ACCOUNTANT-only. Your role ({user?.role}) doesn't have access to these routes.</StatusBanner>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">Bank Reconciliation</h1>
          <p className="page__subtitle">
            Import a bank statement CSV, then match each unmatched line against a candidate ledger transaction leg.
            Matching is a simple date/amount-tolerance window — never automatic.
          </p>
        </div>
      </div>

      <div className="panel">
        <h2 className="panel__title">Account</h2>
        <div className="form-grid">
          <AccountSelect id="bank-account" label="Account" value={accountId} onChange={setAccountId} required />
        </div>
      </div>

      <div className="panel">
        <h2 className="panel__title">Import statement CSV</h2>
        <p className="muted">Columns: date (ISO 8601), description, amount (signed — positive = deposit, negative = withdrawal), optional reference.</p>
        <form onSubmit={handleImport}>
          <div className="form-grid">
            <BranchSelect id="bank-import-branch" label="Branch (optional)" value={importBranchId} onChange={setImportBranchId} allOptionLabel="Tenant-wide" />
            <div className="sl-field">
              <label className="sl-field__label" htmlFor="bank-file">
                CSV file
              </label>
              <input ref={fileInputRef} id="bank-file" className="sl-input" type="file" accept=".csv,text/csv" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
            </div>
          </div>
          {importError ? <StatusBanner kind="error">{importError}</StatusBanner> : null}
          {importResult ? <StatusBanner kind="info">{importResult}</StatusBanner> : null}
          <div className="form-actions">
            <button type="submit" className="sl-button sl-button--primary sl-button--md" disabled={importing || !accountId}>
              {importing ? "Importing…" : "Import statement"}
            </button>
          </div>
        </form>
      </div>

      <div className="panel">
        <h2 className="panel__title">Unmatched lines</h2>
        {!accountId ? (
          <p className="muted">Select an account above to see its unmatched statement lines.</p>
        ) : (
          <>
            {actionError ? <StatusBanner kind="error">{actionError}</StatusBanner> : null}
            {unmatchedState.error ? <StatusBanner kind="error">{unmatchedState.error}</StatusBanner> : null}
            {unmatchedState.loading ? (
              <p className="muted">Loading…</p>
            ) : (
              <DataTable
                columns={columns}
                rows={unmatchedPageData}
                getRowId={(row) => row.line.id}
                pagination={unmatchedPagination}
                onPageChange={setUnmatchedPage}
                emptyMessage="No unmatched lines for this account."
                caption="Unmatched bank statement lines"
              />
            )}
          </>
        )}
      </div>

      <MatchedLinesUnmatchPanel accountId={accountId} />
    </div>
  );
}

/** Small companion panel: since `GET /bank-reconciliation/unmatched` only ever returns
 * unmatched lines, undoing a match requires knowing the line id out of band — this app
 * doesn't have a "list ALL lines" route to browse from (the backend doesn't expose one),
 * so unmatch is offered as a direct-by-id action here rather than faked as a browsable list. */
function MatchedLinesUnmatchPanel({ accountId }: { accountId: string }) {
  const { ensureFreshToken } = useAuth();
  const [lineId, setLineId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  async function handleUnmatch(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSuccess(null);
    setSubmitting(true);
    try {
      const token = await ensureFreshToken();
      const line = await unmatchBankLine(token, lineId.trim());
      setSuccess(`Line ${line.id.slice(0, 8)} is now unmatched — it will reappear in the unmatched list above.`);
      setLineId("");
    } catch (error_) {
      setError(error_ instanceof ApiError ? error_.message : "Could not unmatch — check the line id.");
    } finally {
      setSubmitting(false);
    }
  }

  if (!accountId) return null;

  return (
    <div className="panel">
      <h2 className="panel__title">Undo a match</h2>
      <p className="muted">Paste a bank statement line id (shown truncated elsewhere in this app) to reverse a match — the backend has no "list all matched lines" route yet.</p>
      <form onSubmit={handleUnmatch}>
        <div className="form-grid">
          <input className="sl-input" placeholder="Bank statement line id" value={lineId} onChange={(e) => setLineId(e.target.value)} required />
        </div>
        {error ? <StatusBanner kind="error">{error}</StatusBanner> : null}
        {success ? <StatusBanner kind="info">{success}</StatusBanner> : null}
        <div className="form-actions">
          <button type="submit" className="sl-button sl-button--secondary sl-button--md" disabled={submitting}>
            {submitting ? "Unmatching…" : "Unmatch"}
          </button>
        </div>
      </form>
    </div>
  );
}
